import { cloneDeep } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import {
  aClient,
  aDrawnFeature,
  aFiltersSort,
  aMapViewState,
  aSelectedGeographyLayerFilter,
  useAtomValue,
} from '~/atoms'
import { withLocationFilters } from '~/lib/map-listing-find'
import type { Listing } from '~/models'
import { useListingsQuery } from '~/query'
import { useClientFind } from './useClientFind'

export type MapOutletContextData = {
  listings: Listing[]
  isError: boolean
  isLoading: boolean
  isFetching: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
}

export function useMapListings(): MapOutletContextData {
  const client = useAtomValue(aClient)
  const clientFind = useClientFind(client!)
  const filtersSort = useAtomValue(aFiltersSort)
  const mapViewState = useAtomValue(aMapViewState)
  const drawFeature = useAtomValue(aDrawnFeature)
  const selectedGeographyLayer = useAtomValue(aSelectedGeographyLayerFilter)
  const [queryEnabled, setQueryEnabled] = useState(false)

  useEffect(() => {
    if (mapViewState) {
      setTimeout(() => {
        setQueryEnabled(true)
      }, 100)
    }
  }, [mapViewState])

  const listingsFind = useMemo(() => {
    if (!mapViewState) return {}
    const find: Record<string, any> = cloneDeep(clientFind)
    let locationFilter
    if (drawFeature) {
      locationFilter = {
        $geoWithin: {
          $geometry: drawFeature.geometry,
        },
      }
    } else {
      const sw = Object.values(mapViewState.bounds.getSouthWest())
      const ne = Object.values(mapViewState.bounds.getNorthEast())
      locationFilter = {
        $geoWithin: {
          $geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [sw[0], ne[1]],
                [ne[0], ne[1]],
                [ne[0], sw[1]],
                [sw[0], sw[1]],
                [sw[0], ne[1]],
              ],
            ],
          },
        },
      }
    }
    const selectedLayerLocation = selectedGeographyLayer
      ? { $geoWithin: { $geometry: selectedGeographyLayer.geometry } }
      : null

    return withLocationFilters(find, selectedLayerLocation, locationFilter)
  }, [mapViewState, clientFind, drawFeature, selectedGeographyLayer])

  const {
    listings,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useListingsQuery({
    key: [
      'map-listings',
      'disable-cache',
      JSON.stringify({ ...listingsFind, ...filtersSort }),
    ],
    find: { status: 'active', ...listingsFind },
    sort: filtersSort,
    project: {
      id: 1,
      location: 1,
      created: 1,
      modified: 1,
      name: 1,
      slug: 1,
      property: 1,
      type: 1,
      status: 1,
      property_types: 1,
      images: {
        $cond: {
          if: { $isArray: '$images' },
          then: { $slice: ['$images', 0, 1] },
          else: [],
        },
      },
      // TODO Replace with filters
      meta: {
        acres: 1,
        price: 1,
        cap_rate: 1,
        min_lease_rate: 1,
        max_lease_rate: 1,
        min_available_sf: 1,
        max_available_sf: 1,
        building_sf: 1,
      },
      suites: 1,
      organization: 1,
    },
    limit: 60,
    enabled: queryEnabled,
  })

  return {
    listings,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  }
}
